import { useTranslation } from '@/app/components/translations/Translation';
import type { TimeSlotsFilterChange } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import {
  getTimeSlotFilterChangeData,
  sortTimeSlots,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportTimeSlotFilter';
import { useTransportPageFilterTracking } from '@/app/components/hotel-detail/changeTransport/hooks/useTransportPageFilterTracking';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import {
  EvoBoundHeader,
  EvoCheckboxRow,
  EvoFilterDivider,
  EvoFilterLoadingLayer,
  EvoFilterSection,
} from '@/app/components/hotel-detail/evo/transport/EvoTransportFilters/filters/EvoFilterShared';

const TIME_SLOT_RANGES: { [key in TimeSlotFilterId]: string } = {
  MORNING: '06:00 – 11:59',
  AFTERNOON: '12:00 – 17:59',
  EVENING: '18:00 – 23:59',
  NIGHT: '00:00 – 05:59',
};

const getTimeSlotLabelKey = (id: TimeSlotFilterId, isTrain: boolean): string =>
  `${isTrain ? 'vacation.trainavailability.filters.timeslot' : 'vacation.flightavailability.filters.timeslot'}.${id.toLowerCase()}`;

type EvoTimeSlotFilterProps = {
  outboundTimeSlots?: TimeSlotFilter[];
  inboundTimeSlots?: TimeSlotFilter[];
  onChange: (filterChange: TimeSlotsFilterChange) => void;
};

export function EvoTimeSlotFilter(props: EvoTimeSlotFilterProps) {
  const { outboundTimeSlots, inboundTimeSlots, onChange } = props;
  const { isTrain } = useUrlSearchParams();
  const { t } = useTranslation();
  const { sendCuriosityClickEvent } = useCuriosity();
  const sendStratrackFlightPageFilterEvent = useTransportPageFilterTracking();

  if (!outboundTimeSlots && !inboundTimeSlots) return null;

  const renderBound = (type: BoundType, timeSlots: TimeSlotFilter[]) => (
    <EvoFilterSection>
      <EvoBoundHeader type={type} />
      {sortTimeSlots(timeSlots).map((timeSlot) => (
        <EvoCheckboxRow
          key={timeSlot.id}
          id={`evo-time-slot-${type}-${timeSlot.id}`}
          label={t(getTimeSlotLabelKey(timeSlot.id, isTrain))}
          rightLabel={TIME_SLOT_RANGES[timeSlot.id]}
          checked={timeSlot.selected}
          disabled={!timeSlot.enabled}
          onChange={() =>
            onChange(
              getTimeSlotFilterChangeData(
                type,
                timeSlots,
                timeSlot.id,
                sendCuriosityClickEvent,
                sendStratrackFlightPageFilterEvent
              )
            )
          }
        />
      ))}
      <EvoFilterLoadingLayer origins={[type === 'outbound' ? 'DEP_TIME_OUTBOUND_FILTER' : 'DEP_TIME_RETURN_FILTER']} />
    </EvoFilterSection>
  );

  return (
    <>
      {outboundTimeSlots ? renderBound('outbound', outboundTimeSlots) : null}
      {outboundTimeSlots && inboundTimeSlots ? <EvoFilterDivider /> : null}
      {inboundTimeSlots ? renderBound('inbound', inboundTimeSlots) : null}
    </>
  );
}
